import { FiPlay, FiPause, FiX } from "react-icons/fi";
import { TbRewindBackward10, TbRewindForward10 } from "react-icons/tb";
import { useState, useEffect } from "react";

export default function LearnSpeechControlBar({ 
  isVisible, 
  isPlaying, 
  articleTitle, 
  onPlayPause, 
  onClose,
  currentTime,
  duration,
  onSeek,
  playbackRate,
  onPlaybackRateChange
}) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (duration > 0) {
      setProgress((currentTime / duration) * 100);
    }
  }, [currentTime, duration]);

  const handlePlaybackRateChange = () => {
    if (onPlaybackRateChange) {
      onPlaybackRateChange();
    }
  };

  const handleRewind = () => {
    // Go back 10 seconds
    if (duration > 0) {
      const newTime = Math.max(0, currentTime - 10);
      const percentage = (newTime / duration) * 100;
      if (onSeek) {
        onSeek(percentage);
      }
    }
  };

  const handleFastForward = () => {
    // Go forward 10 seconds
    if (duration > 0) {
      const newTime = Math.min(duration, currentTime + 10);
      const percentage = (newTime / duration) * 100;
      if (onSeek) {
        onSeek(percentage);
      }
    }
  };

  const handleProgressClick = (e) => {
    if (!onSeek || duration === 0) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percentage = (clickX / width) * 100;
    
    onSeek(percentage);
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const remainingTime = duration - currentTime;

  if (!isVisible) return null;

  return (
    <div className="speech-control-bar">
      {/* Header with user info */}
      <div className="speech-control-header">
        <div className="speech-user-info">
          <div className="speech-avatar">
            <div className="speech-heartbeat-logo">
              <svg width="100" height="50" viewBox="0 0 100 50">
                <defs>
                  <linearGradient id="speechHeartbeatGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{ stopColor: 'rgba(255,255,255,0.4)' }} />
                    <stop offset="50%" style={{ stopColor: 'rgba(255,255,255,1)' }} />
                    <stop offset="100%" style={{ stopColor: 'rgba(255,255,255,0.4)' }} />
                  </linearGradient>
                </defs>
                <path
                  d="M 5 25
                     L 25 25
                     L 30 17
                     L 33 33
                     L 38 10
                     L 48 40
                     L 53 22
                     L 58 25
                     L 95 25"
                  fill="none"
                  stroke="url(#speechHeartbeatGradient)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    strokeDasharray: '140',
                    strokeDashoffset: '140',
                    animation: 'drawLine 3s linear infinite 0s'
                  }}
                />
                <circle cx="5" cy="25" r="2" fill="white" />
                <circle cx="95" cy="25" r="2" fill="white" />
              </svg>
            </div>
          </div>
          <div className="speech-user-details">
            <span className="speech-user-trait">Auri Assistant</span>
            <span className="speech-user-name">Health Reader</span>
          </div>
          <div className="speech-avatar">
            <div className="speech-heartbeat-logo">
              <svg width="100" height="50" viewBox="0 0 100 50">
                <defs>
                  <linearGradient id="speechHeartbeatGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" style={{ stopColor: 'rgba(255,255,255,0.4)' }} />
                    <stop offset="50%" style={{ stopColor: 'rgba(255,255,255,1)' }} />
                    <stop offset="100%" style={{ stopColor: 'rgba(255,255,255,0.4)' }} />
                  </linearGradient>
                </defs>
                <path
                  d="M 5 25
                     L 25 25
                     L 30 17
                     L 33 33
                     L 38 10
                     L 48 40
                     L 53 22
                     L 58 25
                     L 95 25"
                  fill="none"
                  stroke="url(#speechHeartbeatGradient2)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    strokeDasharray: '140',
                    strokeDashoffset: '140',
                    animation: 'drawLine 3s linear infinite 0s'
                  }}
                />
                <circle cx="5" cy="25" r="2" fill="white" />
                <circle cx="95" cy="25" r="2" fill="white" />
              </svg>
            </div>
          </div>
        </div>
        <button className="speech-close-button" onClick={onClose}>
          <FiX />
        </button>
      </div>

      {/* Article title */}
      <div className="speech-title">
        <h4>{articleTitle}</h4>
      </div>

      {/* Progress bar */}
      <div className="speech-progress-container">
        <span className="speech-time-current">{formatTime(currentTime)}</span>
        <div 
          className="speech-progress-bar clickable"
          onClick={handleProgressClick}
        >
          <div 
            className="speech-progress-fill" 
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="speech-time-remaining">-{formatTime(remainingTime)}</span>
      </div>

      {/* Playback controls */}
      <div className="speech-controls">
        <button className="speech-control-button" onClick={handleRewind} title="Rewind 10s">
          <TbRewindBackward10 />
        </button>
        
        <button 
          className="speech-play-button"
          onClick={() => onPlayPause()}
          title={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? <FiPause /> : <FiPlay />}
        </button>
        
        <button className="speech-control-button" onClick={handleFastForward} title="Forward 10s">
          <TbRewindForward10 />
        </button>

        <button 
          className="speech-rate-button"
          onClick={handlePlaybackRateChange}
          title="Playback speed"
        >
          {playbackRate}x
        </button>
      </div>
    </div>
  );
}
